import { useCallback } from 'react';
import { NativeModules } from 'react-native';

import { NavigationProp } from '@react-navigation/native';

import { ENV, PROD } from '@/data';
import { RootStackParamList } from '@/navigation/types';
import { isIos } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';

import { UserInfo } from '../use-auth-user-info';
import { useUserStore } from '../use-user-store';

const { PaysafeVenmoSDKModule } = NativeModules;

const LOG_TAG = '[PaysafeVenmoPaymentsSDK]';

// This is to initialize the Venmo context in iOS.
// For android, it will be initialized within MainActivity.kt
export const useInitializeVenmoContext = () => {
    const initializeVenmoContext = useCallback(async (): Promise<string | undefined> => {
        if (isIos) {
            try {
                const result = await PaysafeVenmoSDKModule.initializeVenmoContext();
                return result;
            } catch (error) {
                logger.error(LOG_TAG, 'Failed to initialize Venmo context', error);
            }
        } else {
            logger.debug(LOG_TAG, 'For android, Venmo will be initialized within MainActivity.kt');
        }
    }, []);

    return initializeVenmoContext;
};

/**
 * Handles venmo tokenization. If tokenization is successful, return a venmo payment token.
 */
export const handleTokenizePayment = async (
    amount: number,
    paysafeAccountId: string,
    userInfo: UserInfo['data'],
    navigate: Omit<NavigationProp<RootStackParamList>, 'getState'>['navigate']
) => {
    const totalPrice = amount;
    const consumerId = useUserStore.getState().profile.sub;
    const firstName = userInfo?.first_name;
    const lastName = userInfo?.last_name;
    const email = userInfo?.email;
    const address = userInfo?.address ?? {
        country: '',
        post_code: '',
        state: '',
        city: '',
        street_name: '',
    };

    try {
        const response = await PaysafeVenmoSDKModule.tokenizeVenmoPayment(
            totalPrice,
            paysafeAccountId,
            getVenmoProfileId(),
            consumerId,
            firstName,
            lastName,
            email,
            address
        );

        return response.trim();
    } catch (error) {
        logger.error(LOG_TAG, 'Tokenization Failure:', error);
        navigate('ErrorModal', {
            title: 'Error Processing Payment',
            subtitle: 'There is an error tokenizing your payment.',
            primaryButton: 'Close',
        });
    }
};

const getVenmoProfileId = (): string => {
    const PROFILE_IDS = {
        FANTASY: '3978253020257600839',
        SANDBOX: '1953896702662410263',
    };

    return ENV === PROD ? PROFILE_IDS.FANTASY : PROFILE_IDS.SANDBOX;
};
